/**
 * FormDialog - Reusable form dialog component
 *
 * Reduces duplication for form dialogs
 */

import { Button } from "@chakra-ui/react"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
} from "@chakra-ui/react"
import React from "react"

export interface FormDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void | Promise<void>
  title: string
  submitText?: string
  cancelText?: string
  submitColorScheme?: string
  isLoading?: boolean
  isSubmitDisabled?: boolean
  children: React.ReactNode
  size?: "sm" | "md" | "lg" | "xl"
}

export const FormDialog = React.memo(
  ({
    isOpen,
    onClose,
    onSubmit,
    title,
    submitText = "Submit",
    cancelText = "Cancel",
    submitColorScheme = "blue",
    isLoading = false,
    isSubmitDisabled = false,
    children,
    size = "md",
  }: FormDialogProps) => {
    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()
      await onSubmit()
      if (!isLoading) {
        onClose()
      }
    }

    return (
      <DialogRoot
        open={isOpen}
        onOpenChange={(e) => !e.open && onClose()}
        size={{ base: "full", md: size }}
      >
        <Portal>
          <DialogBackdrop />
          <DialogPositioner>
            <DialogContent as="form" onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{title}</DialogTitle>
                <DialogCloseTrigger />
              </DialogHeader>
              <DialogBody>{children}</DialogBody>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={isLoading}
                >
                  {cancelText}
                </Button>
                <Button
                  type="submit"
                  colorScheme={submitColorScheme}
                  loading={isLoading}
                  disabled={isSubmitDisabled}
                  ml={3}
                >
                  {submitText}
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
    )
  },
)

FormDialog.displayName = "FormDialog"

export default FormDialog
